import React, { useState, useRef } from "react";
import "./LasgidiGotTalent.css";

const LasgidiGotTalent = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    over18: null,
    guardianName: "",
    guardianContact: "",
    confirmAge: false,
    videoFile: null,
    videoLink: "",
    talentType: "",
    otherTalent: "",
  });

  const [loading, setLoading] = useState(false);
  const [showGuardianField, setShowGuardianField] = useState(false);
  const fileInputRef = useRef(null);

  const SpinnerOverlay = () => (
    <div className="spinner-overlay">
      <div className="spinner"></div>
    </div>
  );

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name === "over18") return;

    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (type === "file") {
      const file = files[0];
      if (file) {
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          alert("The selected file exceeds 100MB. Please upload a smaller video.");
          e.target.value = "";
          setFormData({ ...formData, videoFile: null });
          return;
        }
        setFormData({ ...formData, videoFile: file });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTalentChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      talentType: value,
      otherTalent: value !== "Other" ? "" : prev.otherTalent,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let uploadedFilename = "";

    try {
      // --- Upload video if selected ---
      if (formData.videoFile) {
        const videoForm = new FormData();
        videoForm.append("videoFile", formData.videoFile);

        const uploadResponse = await fetch("/php/upload-video.php", {
          method: "POST",
          body: videoForm,
        });

        const uploadResult = await uploadResponse.json();
        if (uploadResult.success) {
          uploadedFilename = uploadResult.url; // full URL from backend
        } else {
          throw new Error(uploadResult.message || "Video upload failed.");
        }
      }

      // --- Prepare all form data for backend ---
      const data = new FormData();
      data.append("name", formData.name);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("talentType", formData.talentType === "Other" ? formData.otherTalent : formData.talentType);
      data.append("over18", formData.over18 ? "true" : "false");
      data.append("confirmAge", formData.confirmAge ? "1" : "");
      data.append("guardianName", formData.guardianName);
      data.append("guardianContact", formData.guardianContact);
      data.append("videoFile", uploadedFilename);
      data.append("videoLink", formData.videoLink);

      const response = await fetch("/php/lasgidi-got-talent.php", {
        method: "POST",
        body: data,
      });

      const result = await response.json();

      if (result.success) {
        alert("Application submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          over18: null,
          guardianName: "",
          guardianContact: "",
          confirmAge: false,
          videoFile: null,
          videoLink: "",
          talentType: "",
          otherTalent: "",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        alert(result.message || "Submission failed.");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      alert("An error occurred while submitting the form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="talent-page page-container">
      {loading && <SpinnerOverlay />}

      <form
        className="talent-form"
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        <div className="talent-page-header">
          <h1>Lasgidi Got Talent: Registration</h1>
          <p>Fill out the form below to register your interest before the event date is announced.</p>
        </div>

        <label>Name:</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <label>Email:</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <label>Phone:</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
        />

        <label>What talent will you display?</label>
        <select
          name="talentType"
          value={formData.talentType}
          onChange={handleTalentChange}
          required
        >
          <option value="" disabled>Select your talent</option>
          <option value="Singing">Singing</option>
          <option value="Dance">Dance</option>
          <option value="Poetry / Spoken Word">Poetry / Spoken Word</option>
          <option value="Comedy">Comedy</option>
          <option value="Acting">Acting</option>
          <option value="Instrumental Performance">Instrumental Performance</option>
          <option value="Art">Art</option>
          <option value="Other">Other</option>
        </select>

        {formData.talentType === "Other" && (
          <>
            <label>Please specify your talent:</label>
            <input
              type="text"
              name="otherTalent"
              value={formData.otherTalent}
              onChange={(e) =>
                setFormData({ ...formData, otherTalent: e.target.value })
              }
              required
            />
          </>
        )}

        <label>Upload Video Clip (100MB max) or YouTube Link:</label>
        <input
          type="file"
          name="videoFile"
          accept="video/*"
          onChange={handleChange}
          ref={fileInputRef}
        />
        <input
          type="url"
          name="videoLink"
          value={formData.videoLink}
          placeholder="YouTube Link"
          onChange={handleChange}
        />

        <label className="question-label">Are you 18 years or older?</label>
        <div className="age-toggle">
          <input
            type="radio"
            id="ageYes"
            name="over18"
            checked={formData.over18 === true}
            onChange={() => {
              setFormData({
                ...formData,
                over18: true,
                guardianName: "",
                guardianContact: "",
              });
              setShowGuardianField(false);
            }}
          />
          <label htmlFor="ageYes">Yes</label>

          <input
            type="radio"
            id="ageNo"
            name="over18"
            checked={formData.over18 === false}
            onChange={() => {
              setFormData({ ...formData, over18: false });
              setShowGuardianField(true);
            }}
          />
          <label htmlFor="ageNo">No</label>
        </div>

        {showGuardianField && (
          <>
            <label>Parent/Guardian Name:</label>
            <input
              type="text"
              name="guardianName"
              value={formData.guardianName}
              onChange={handleChange}
              required
            />

            <label>Parent/Guardian Contact:</label>
            <input
              type="text"
              name="guardianContact"
              value={formData.guardianContact}
              onChange={handleChange}
              required
            />
          </>
        )}

        <label>
          <input
            type="checkbox"
            name="confirmAge"
            checked={formData.confirmAge}
            onChange={handleChange}
          />
          I confirm that the information provided is accurate.
        </label>

        <button type="submit" className="post-review-btn lgt-submit-btn">
          Submit Application
        </button>
      </form>
    </div>
  );
};

export default LasgidiGotTalent;
